import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, ArrowRight, CheckCircle2, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import logoUrbnet from "@/assets/images/UrbNetLogoSemFundo.png";

const FloatingOrb = ({ className, delay = 0 }: { className?: string; delay?: number }) => (
  <motion.div
    className={`absolute rounded-full blur-3xl opacity-30 ${className}`}
    animate={{
      y: [0, -30, 0],
      x: [0, 15, 0],
      scale: [1, 1.1, 1],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

export default function EsqueceuSenhaPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, informe seu e-mail.",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "E-mail inválido",
        description: "Por favor, informe um e-mail válido.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const dataSource = new RemoteDataSource(Settings.urlSecondary);
      
      const result = await dataSource.post({
        endpoint: "v1/usuarios/esqueceu-senha",
        body: {
          id: email,
        },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao enviar e-mail",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao enviar o e-mail. Tente novamente.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      setEmailSent(true);
      toast({
        title: "E-mail enviado!",
        description: "Enviamos instruções para redefinir sua senha.",
      });

      // Redirecionar para página de verificação de código
      setTimeout(() => {
        navigate(`/autenticacao-dois-fatores?email=${encodeURIComponent(email)}`);
      }, 1000);
    } catch (error) {
      toast({
        title: "Erro ao enviar e-mail",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-gradient-to-br from-background via-background to-background">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <FloatingOrb className="w-96 h-96 bg-primary/40 -top-48 -left-48" delay={0} />
        <FloatingOrb className="w-80 h-80 bg-secondary/40 top-1/3 -right-40" delay={2} />
        <FloatingOrb className="w-64 h-64 bg-primary/30 bottom-0 left-1/4" delay={4} />
        
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
      </div>

      {/* Left Panel - Desktop */}
      <div className="hidden lg:flex flex-col justify-center items-center flex-1 relative z-10 px-16">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-lg"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-8"
          >
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-20 object-contain"
            />
          </motion.div>

          {/* Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="w-20 h-20 rounded-2xl mb-6 flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--secondary) / 0.15))",
              border: "1px solid hsl(var(--primary) / 0.2)"
            }}
          >
            <LockKeyhole className="h-10 w-10 text-primary" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="font-heading text-5xl font-bold tracking-tight leading-tight mb-6"
          >
            <span className="text-foreground">Esqueceu sua </span>
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
            >
              senha?
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg text-muted-foreground leading-relaxed mb-10"
          >
            Não se preocupe! Informe seu e-mail e enviaremos instruções
            para você redefinir sua senha e acessar sua conta.
          </motion.p>

          {/* Info Cards */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="flex items-start gap-4 p-4 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">Verifique seu e-mail</h3>
                <p className="text-xs text-muted-foreground">
                  Você receberá um link de recuperação em alguns minutos
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="flex items-start gap-4 p-4 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">Link válido por 30 minutos</h3>
                <p className="text-xs text-muted-foreground">
                  O link de recuperação expira após 30 minutos por segurança
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="relative">
            {/* Card glow effect */}
            <div 
              className="absolute -inset-1 rounded-3xl blur-xl opacity-20"
              style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
            />
            
            <div className="relative p-8 rounded-3xl border border-border/50 bg-card/80 backdrop-blur-xl shadow-2xl">
              {/* Mobile Logo */}
              <div className="lg:hidden flex justify-center mb-6">
                <img 
                  src={logoUrbnet} 
                  alt="UrbNet Logo" 
                  className="h-16 object-contain"
                />
              </div>

              {/* Header */}
              <div className="mb-8">
                <motion.button
                  whileHover={{ x: -3 }}
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </motion.button>
                <h2 className="font-heading text-2xl font-bold text-foreground">
                  {emailSent ? "E-mail enviado" : "Recuperar senha"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {emailSent 
                    ? "Verifique sua caixa de entrada para as instruções"
                    : "Informe seu e-mail para recuperar sua conta"
                  }
                </p>
              </div>

              {!emailSent ? (
                /* Form */
                <form onSubmit={handleSubmit} className="space-y-5">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      E-mail
                    </Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu.email@exemplo.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField(null)}
                        className={`h-12 rounded-xl transition-all duration-300 ${
                          focusedField === "email" 
                            ? "ring-2 ring-primary/50 border-primary" 
                            : "border-border/50"
                        }`}
                      />
                      {email && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                    className="pt-2"
                  >
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="relative w-full h-14 rounded-xl font-semibold text-base overflow-hidden group"
                      style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))",
                      }}
                    >
                      <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {isLoading ? (
                        <motion.div
                          className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <span className="relative flex items-center gap-2">
                          Enviar instruções
                          <ArrowRight className="h-5 w-5" />
                        </span>
                      )}
                    </Button>
                  </motion.div>
                </form>
              ) : (
                /* Success State */
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="text-center py-8"
                >
                  <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 text-success" />
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Enviamos as instruções para <strong>{email}</strong>
                  </p>
                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full h-12 rounded-xl font-semibold"
                    style={{
                      background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))",
                    }}
                  >
                    Voltar ao login
                  </Button>
                </motion.div>
              )}

              {/* Footer */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="text-xs text-muted-foreground text-center mt-6"
              >
                Lembrou sua senha?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-primary hover:underline font-medium"
                >
                  Fazer login
                </button>
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
